import { type StreamMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

declare global {
	interface Window {
		/**
		 * Set to `true` to enable recording of raw SSE stream responses for debugging.
		 * Must be enabled before sending a message for recording to take effect.
		 */
		__rovoChatResponseDebugger__: boolean | undefined;
		/**
		 * Array of raw stream response messages recorded during the latest SSE stream session.
		 * Each entry is a JSON-stringified StreamMessage
		 * This is populated automatically as stream messages are received.
		 */
		__rovoStreamResponseMessages__: string[] | undefined;
		/**
		 * Returns the recorded stream response messages as newline-delimited JSON,
		 * ready to be copied/pasted into examples or tests.
		 */
		__rovoStreamResponseOutput__: (() => string) | undefined;
	}
}

/**
 * Resets the stream response recording for a new stream session.
 * Should be called at the start of each `processStream` invocation.
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const resetStreamResponseRecording = (): void => {
	try {
		if (window.top) {
			window.top.__rovoStreamResponseMessages__ = [];
		}
	} catch {
		// Ignore cross-origin frame access errors
	}
};

/**
 * Records a single stream message by JSON-stringifying it and appending
 * to the `window.top.__rovoStreamResponseMessages__` array.
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const recordStreamMessage = (message: StreamMessage): void => {
	try {
		if (window.top) {
			if (!window.top.__rovoStreamResponseMessages__) {
				window.top.__rovoStreamResponseMessages__ = [];
			}
			const serialised = JSON.stringify(message);
			window.top.__rovoStreamResponseMessages__.push(serialised);
		}
	} catch {
		// Ignore cross-origin frame access errors
	}
};

/**
 * Returns the recorded stream response messages as newline-delimited JSON strings.
 *
 * Wrapped in a try/catch because accessing properties on `window.top` can throw a
 * `SecurityError` when the caller is in a cross-origin iframe (e.g. the Rovo Chrome
 * Extension side panel). See ASIMO-3773 / PIR-300295.
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const getStreamResponseOutput = (): string => {
	try {
		const messages = window.top?.__rovoStreamResponseMessages__ ?? [];
		return messages.join('\n');
	} catch {
		// Ignore cross-origin frame access errors
		return '';
	}
};

/**
 * Installs the `__rovoStreamResponseOutput__` getter on `window.top`.
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const installStreamResponseOutputGetter = (): void => {
	try {
		if (window.top) {
			window.top.__rovoStreamResponseOutput__ = getStreamResponseOutput;
		}
	} catch {
		// Ignore cross-origin frame access errors
	}
};

installStreamResponseOutputGetter();
